import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import InboxInquiryAssignment from './InboxInquiryAssignment'
import type { InboxItem, CompanyRow } from '../../api/types'

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export default function Inbox() {
  const [error, setError] = useState('')
  const [items, setItems] = useState<InboxItem[]>([])
  const [message, setMessage] = useState<Awaited<ReturnType<typeof api.inboxMessage>> | null>(null)
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<CompanyRow[]>([])
  const [company, setCompany] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  async function open(id: string) {
    setBusy(true); setError(''); setMessage(null); setMatches([]); setCompany(''); setReason('')
    try { setMessage(await api.inboxMessage(id)) } catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  async function search() {
    setBusy(true); setError('')
    try { setMatches((await api.companies({ q: query, limit: '20' })).rows) } catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  async function assign(event: React.FormEvent) {
    event.preventDefault(); if (!message || !company) return; setBusy(true); setError('')
    try { await api.assignInbox(message.id, company, reason); setMessage(await api.inboxMessage(message.id)); setItems(await api.inbox()) }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }

  useEffect(() => {
    api.inbox().then(setItems).catch(e => setError(e.message))
  }, [])

  return (
    <>
      {error && <div className="notice crit" role="alert">{error}</div>}
      <div className="app-head">
        <h1>Inbox</h1>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {items.length} recent messages
        </span>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Received</th>
              <th>Company</th>
              <th>Kind</th>
              <th>Subject</th>
              <th>Classification</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.item_id}>
                <td>{when(i.received_at)}</td>
                <td className="wrap">
                  {i.company_id ? <Link to={`/app/companies/${i.company_id}`}>{i.company_name}</Link> : i.company_name}
                </td>
                <td>{i.kind === 'inquiry' ? 'website inquiry' : i.kind === 'email' ? 'email' : 'call note'}</td>
                <td className="wrap">
                  <button className="btn secondary small" disabled={busy} onClick={() => open(i.item_id)}>{i.subject || 'Open message'}</button>
                  <br />
                  <span className="muted">{i.preview}</span>
                </td>
                <td>
                  <span className={`pill${i.classification === 'take a message' ? ' warn' : ''}`}>{i.classification}</span>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Nothing waiting.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {message && <section className="panel" style={{ marginTop: '1.5rem' }}>
        <h2>{message.subject}</h2><p>From {message.sender}</p>
        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.body}</p>
        {message.attachments.map(a => <p key={a.artifact_id}><button className="btn secondary small" onClick={async () => { try { const link = await api.artifactUrl(a.artifact_id); window.open(link.url, '_blank', 'noopener,noreferrer') } catch (e) { setError((e as Error).message) } }}>{a.filename}</button></p>)}
        {message.company_id && message.id.startsWith('email:') && <InboxInquiryAssignment key={`${message.id}:${message.company_id}`} messageId={message.id} companyId={message.company_id} saved={async () => {
          const [fresh, inbox] = await Promise.all([api.inboxMessage(message.id), api.inbox()])
          setMessage(previous => previous?.id === fresh.id ? fresh : previous); setItems(inbox)
        }} />}
        {message.id.startsWith('inquiry:') ? <p className="muted">General website inquiry. Vendor RFP extraction does not apply.</p> : message.company_id ? <Link to={`/app/companies/${encodeURIComponent(message.company_id)}`}>Open company</Link> : <>
          <h3>Assign to a company</h3>
          <label>Company name or domain <input value={query} onChange={e => setQuery(e.target.value)} /></label>
          <button className="btn secondary small" disabled={busy || !query.trim()} onClick={search}>Find company</button>
          <form onSubmit={assign}>
            {matches.map(c => <label key={c.company_id} style={{ display: 'block' }}><input type="radio" name="assign-company" value={c.company_id} checked={company === c.company_id} onChange={() => setCompany(c.company_id)} /> {c.name} · {c.domain}</label>)}
            <label>Reason for assignment <textarea required value={reason} onChange={e => setReason(e.target.value)} maxLength={2000} /></label>
            <button className="btn secondary" disabled={busy || !company}>Assign and process</button>
          </form>
        </>}
      </section>}
    </>
  )
}
